import { Breadcrumbs } from "@heroui/react";
import { useLocation } from "react-router";

import { findNavItem } from "@/src/components/layout/navigation";
import { useBreadcrumbLeaf } from "@/src/contexts/BreadcrumbContext";
import { useTranslations } from "@/src/i18n/i18n";

/**
 * Où l'on est, et par où l'on est passé.
 *
 * Deux maillons au plus, et c'est suffisant : l'arborescence de l'admin est
 * plate — une section, éventuellement un élément dedans. « Tableau de bord >
 * Comptes > nolan.dice » ferait passer la racine pour une étape alors qu'aucun
 * écran n'est *dans* le tableau de bord.
 *
 * - la **section** vient de `NAVIGATION`, comme le titre avant elle : un écran
 *   n'a rien à déclarer pour y figurer, et le fil ne peut pas diverger du
 *   libellé du lien qui y mène ;
 * - le **dernier maillon** vient de l'écran, via `BreadcrumbContext` — lui seul
 *   sait qu'un identifiant de compte se dit « nolan.dice ».
 *
 * ⚠️ **Le dernier maillon n'est pas un lien.** Un lien vers la page courante
 * n'emmène nulle part, et se présente pourtant comme une sortie. React Aria
 * marque le dernier `aria-current="page"` à la place.
 */
export function AppBreadcrumb() {
  const { pathname } = useLocation();
  const tn = useTranslations("navigation");
  const tg = useTranslations("global");
  const ta = useTranslations("a11y");

  const section = findNavItem(pathname);
  const leaf = useBreadcrumbLeaf();

  // Hors navigation connue (la 404 sous session, par exemple) : le nom du
  // produit vaut mieux qu'un fil vide.
  const sectionLabel = section ? tn(section.key) : tg("app-name");

  // Le maillon final ne s'affiche que sur une **sous-page** de la section :
  // sur `/accounts` lui-même, il ferait doublon avec la section.
  const isLeafPage = section !== undefined && pathname !== section.path;
  const hasLeaf = isLeafPage && leaf !== null;

  return (
    <>
      {/*
        Le titre reste un `h1`, invisible mais présent : le fil d'Ariane est une
        `nav`, pas un titre, et une page sans `h1` casse le plan du document —
        les cartes des écrans sont des `h2`, elles auraient un trou au-dessus
        d'elles.
      */}
      <h1 className="sr-only">
        {hasLeaf ? `${sectionLabel} — ${leaf}` : sectionLabel}
      </h1>

      {/*
        ⚠️ **La `nav` est à nous.** `Breadcrumbs` de HeroUI rend une simple
        `<ol>` et ne porte pas le rôle de navigation : sans cette enveloppe, le
        fil n'est qu'une liste parmi quatre dans la page, impossible à viser
        pour qui navigue par points de repère.
      */}
      <nav aria-label={ta("breadcrumb")} className="min-w-0">
        <Breadcrumbs className="min-w-0">
          <Breadcrumbs.Item
            className="truncate text-lg font-semibold tracking-tight"
            href={hasLeaf && section ? section.path : undefined}
          >
            {sectionLabel}
          </Breadcrumbs.Item>

          {hasLeaf ? (
            <Breadcrumbs.Item className="min-w-0 truncate text-lg font-semibold tracking-tight">
              {leaf}
            </Breadcrumbs.Item>
          ) : null}
        </Breadcrumbs>
      </nav>
    </>
  );
}
